/**
 * Money helpers and the fare/tip breakdown shown on the payment sheet
 * (DRIVERAPP_HOSTED_PAGE_PAYMENTS.md §"Fare and tip presentation").
 *
 * All arithmetic is done in integer cents. The breakdown itself is the SERVER's:
 * paymentAPI.summarizeTrip() ships baseFare / tolls / cardSurcharge / persistedTip /
 * cardAmount / walletAmount plus the has* flags whenever rate rows exist. We read those
 * verbatim. Only when a payload predates them (the Live JOB `payment` block carries a
 * subset) do we fall back to the server-tagged price rows (`paymentItem` =
 * 'toll' | 'cardSurcharge' | 'tip', stamped by AutoDispatch) using the same subtraction
 * order the server uses — never a name match or a percentage of our own.
 */

export function toCents(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 100);
}

export function fromCents(cents) {
  return Math.round(Number(cents) || 0) / 100;
}

export function formatMoney(dollars) {
  const cents = toCents(dollars);
  const sign = cents < 0 ? '-' : '';
  return `${sign}$${(Math.abs(cents) / 100).toFixed(2)}`;
}

export function formatCents(cents) {
  return formatMoney(fromCents(cents));
}

/** Percentage tip in cents, rounded to the nearest cent. */
export function percentTipCents(amountCents, percent) {
  return Math.round((Math.max(0, Number(amountCents) || 0) * Number(percent)) / 100);
}

function firstDefined(...values) {
  for (const v of values) {
    if (v !== undefined && v !== null && v !== '') return v;
  }
  return undefined;
}

function isTrue(v) {
  return v === true || v === 1 || String(v).toLowerCase() === 'true';
}

function rowAmountCents(row) {
  if (!row || typeof row !== 'object') return 0;
  return toCents(firstDefined(row.TripAmount, row.tripAmount, row.rate, row.Rate, row.Amount, row.amount));
}

function rowPaymentItem(row) {
  return String((row && (row.paymentItem || row.PaymentItem)) || '').trim().toLowerCase();
}

/**
 * The server's Trip_Price row id, which `requestTripCollection`'s `items[]` must quote
 * back. AD only UPDATEs pre-existing rows matched by id — it cannot create a charge
 * from an id it did not issue — so a row without one is not editable at collection time.
 */
export function rowId(row) {
  const id = firstDefined(row && row.id, row && row.Id, row && row.tripPriceId, row && row.TripPriceId);
  return id === undefined ? null : String(id);
}

/** A row's display name, for labelling an editable charge line. */
export function rowName(row) {
  const name = firstDefined(row && row.name, row && row.Name, row && row.description, row && row.Description);
  return name === undefined ? '' : String(name).trim();
}

/**
 * The rows a driver may edit in the collection sheet, in the server's own SortOrder/Id
 * order.
 *
 * Excluded:
 *  - rows with no server id (nothing to address the update to);
 *  - tip and card-surcharge rows, which AD derives itself from `tipAmount` and the
 *    configured surcharge. Submitting them as items would double-count the money that
 *    `tipAmount` already carries.
 *
 * Tolls are deliberately INCLUDED: the contract says to carry existing tolls inside
 * `fareAmount` and to send their item id when changing them.
 *
 * @returns {{id:string, name:string, amountCents:number, paymentItem:string}[]}
 */
export function editableChargeRows(priceItems) {
  if (!Array.isArray(priceItems)) return [];
  const out = [];
  for (const row of priceItems) {
    const kind = rowPaymentItem(row);
    if (kind === 'tip' || kind === 'cardsurcharge') continue;
    const id = rowId(row);
    if (!id) continue;
    out.push({
      id,
      name: rowName(row),
      amountCents: rowAmountCents(row),
      paymentItem: kind,
    });
  }
  return out;
}

/**
 * Build the `items[]` for `requestTripCollection` from the driver's edits.
 *
 * Only CHANGED rows are sent: the contract states omitted charges remain intact, so a
 * full list would be redundant and would risk re-asserting a value the server has since
 * recalculated. Amounts are absolute calculated charges, never unit rates or deltas.
 *
 * @param {Array}  baseRows  from {@link editableChargeRows}
 * @param {object} editsById {[id]: amountCents}
 * @returns {{id:string, amount:number}[]}
 */
export function buildCollectionItems(baseRows, editsById) {
  if (!Array.isArray(baseRows) || !editsById) return [];
  const items = [];
  for (const row of baseRows) {
    if (!Object.prototype.hasOwnProperty.call(editsById, row.id)) continue;
    const next = Math.max(0, Math.round(Number(editsById[row.id]) || 0));
    if (next === row.amountCents) continue;
    items.push({ id: row.id, amount: fromCents(next) });
  }
  return items;
}

/**
 * @param {object} args
 * @param {object|null} args.payment   Server payment block for the trip (Live `payment`,
 *   Itinerary `reservation`, or a summarizeTrip() row from /API/Payment/active-trips).
 * @param {Array}  [args.priceItems]   Server price rows (Live `pricing`, Itinerary `priceItems`).
 * @param {number} [args.fallbackFare] Last-resort total (e.g. the confirmed meter total).
 * @returns {{
 *   baseFare:number, tolls:number, cardSurcharge:number, persistedTip:number,
 *   hasTolls:boolean, hasCardSurcharge:boolean, hasPersistedTip:boolean,
 *   cardAmount:number, walletAmount:number, total:number, tripCost:number|null,
 *   hasAmount:boolean, source:string
 * }}  Dollar values (2dp). `cardAmount` is what a hosted card page charges before tip;
 *   `walletAmount` (base + tolls) is the cash-equivalent due — it is the fare payable by
 *   wallet, NOT a wallet balance, and must never be shown as available funds.
 *   `tripCost` is the database Trip.TripCost when the server reported one, kept separate
 *   from `total` because the two can legitimately disagree.
 */
export function resolvePaymentBreakdown({ payment, priceItems, fallbackFare } = {}) {
  const p = payment && typeof payment === 'object' ? payment : {};

  // Trip.TripCost as the database holds it, reported SEPARATELY from the price-row
  // total. AD ships both precisely because they can disagree (a quoted trip whose
  // computed rows were never reconciled), and the contract is explicit that neither
  // value may be substituted for the other. Display only — the amount actually
  // collected still comes from the reconciled breakdown below.
  const tripCostCents = p.tripCost === undefined || p.tripCost === null
    ? null
    : toCents(p.tripCost);

  // Is the server's own total trustworthy yet? Its whole breakdown is derived
  // from Trip.TripCost, which is a placeholder until the trip is priced — every
  // flag trip before its drop-off price, and any metered trip before PRICE|Ok.
  // In that window a caller-supplied fare (the flag-trip quote, the confirmed
  // meter total) is the better number and must win, or the sheet shows the
  // placeholder as the fare due.
  const priceIsFinal = isTrue(p.priceFinal);
  const fallbackCents = fallbackFare === undefined || fallbackFare === null
    ? 0
    : Math.max(0, toCents(fallbackFare));
  const serverCardCents = p.cardAmount === undefined || p.cardAmount === null
    ? null
    : toCents(p.cardAmount);
  // Priced rate rows are proof the server has actually costed this trip, even when
  // `priceFinal` has not flipped yet (it is confirmed asynchronously — a metered trip
  // stays priceFinal:false until PRICE|Ok lands, and a Drivers-room push can arrive
  // with the rows but the older flag). Without this, a trip whose rows already sum to
  // the real fare was still overridden by whatever local figure `fallbackFare` held —
  // the stale booking price — so the sheet disagreed with the RiderApp QR page, which
  // reads the same server summary and got it right.
  // Only rows that represent FARE count as proof of costing. Tagged component rows
  // (toll / cardSurcharge / tip) are add-ons that also exist on a trip whose fare is
  // still a placeholder — an unpriced flag trip can already carry a bridge toll and a
  // CC fee — so counting them would wrongly treat that trip as priced and show the
  // placeholder instead of the caller's known quote.
  const fareRowCents = (Array.isArray(priceItems) ? priceItems : []).reduce((sum, row) => {
    const kind = rowPaymentItem(row);
    if (kind === 'toll' || kind === 'tolls' || kind === 'cardsurcharge' || kind === 'tip') return sum;
    return sum + rowAmountCents(row);
  }, 0);
  const rowCents = (Array.isArray(priceItems) ? priceItems : [])
    .reduce((sum, row) => sum + rowAmountCents(row), 0);
  const serverHasPricedRows = fareRowCents > 0;
  // The rider-facing total (TripCost) when the server has genuinely costed the trip:
  // every priced row it shipped, fare rows plus tagged add-ons. Null when there are no
  // fare rows — tagged-only rows (a toll / CC fee on an unpriced trip) are not a costing
  // and must not be mistaken for the whole fare.
  const rowTotalCents = serverHasPricedRows ? rowCents : null;

  // Only override when the server has NOT priced the trip AND its total differs from
  // what we know locally. A priced trip always wins, even when it disagrees with a
  // stale local figure — the server is the authority once it has costed the trip.
  const serverTotalIsStale =
    !priceIsFinal && !serverHasPricedRows && fallbackCents > 0 &&
    (serverCardCents === null || serverCardCents !== fallbackCents);

  // 1) Server-supplied breakdown (preferred; includes the configured CC fee).
  if (serverCardCents !== null && !serverTotalIsStale) {
    const tolls = toCents(p.tolls);
    const fee = toCents(p.cardSurcharge);
    const tip = toCents(p.persistedTip);
    let base = toCents(p.baseFare);
    let card = toCents(p.cardAmount);
    let wallet = p.walletAmount !== undefined && p.walletAmount !== null
      ? toCents(p.walletAmount)
      : Math.max(0, card - fee);
    let source = 'server';

    // This breakdown is display metadata only: AD derives every field of it by
    // SUBTRACTION from a single input, Trip.TripCost (summarizeTripRateItems), and the
    // Live JOB payload builds it WITHOUT applyPaymentRateItems() — so while the trip
    // cache still holds the pre-PRICE booking total, cardAmount is that stale figure
    // even though the rate rows shipped beside it are the real costing. AD treats those
    // rows as "the payment authority" and recomputes the fare from them for the
    // RiderApp/HBSS page, so we reconcile to them here too: the driver collects what the
    // rider pays, and the header total can never read less than the charges listed
    // beneath it.
    // Reconcile UPWARDS ONLY. The rows raise a stale, too-small total; they must never
    // pull down a larger one.
    //
    // Both directions occur in the wild, and only one of them is safe to follow:
    //  - METERED trip: the cache holds the pre-PRICE booking figure while the rows carry
    //    the trip's full costing. The rows are larger and correct -> take them.
    //  - QUOTED / On-Demand trip (HBSS `@PriceIsFinal`): the trip-create SP writes the
    //    quoted TripCost verbatim and leaves its COMPUTED line items at their own
    //    calculated value (a Mileage row of 2.50 on a 26.00 trip), so the rows are an
    //    incomplete remainder that was never reconciled to the quote
    //    (dispatch.applyQuotedTripPrice does that write, and it only runs for rates in
    //    appVar.autoPriceRates). The rows are smaller and WRONG -> keep the server total.
    //
    // A sum of displayed rows that is short of the total is a server-side pricing gap we
    // cannot repair here; the sheet lists what it was given and still collects the
    // authoritative fare. Understating what the driver collects is the worse failure.
    if (rowTotalCents !== null && rowTotalCents > card + tip) {
      card = Math.max(0, rowTotalCents - tip);
      wallet = Math.max(0, card - fee);
      base = Math.max(0, wallet - tolls);
      source = 'priceItems';
    }

    return build({
      tripCostCents,
      base, tolls, fee, tip, card, wallet,
      hasTolls: isTrue(p.hasTolls),
      hasCardSurcharge: isTrue(p.hasCardSurcharge),
      hasPersistedTip: isTrue(p.hasPersistedTip),
      total: card + tip,
      hasAmount: true,
      source,
    });
  }

  // 2) Total from the payment block (or caller), tagged rows for the components.
  const rows = Array.isArray(priceItems) ? priceItems : [];
  let totalCents = null;
  // Set when the block total OUTRANKED a shorter row sum (the quoted-trip case), so
  // `source` does not claim the rows supplied a figure they disagree with. Rows that
  // agree with the block total still count as the source — they described the money.
  let blockTotalWon = false;
  const blockTotal = firstDefined(p.paymentAmount, p.fare, p.price);
  // A known local fare outranks a not-yet-final server total, for the same
  // reason as branch 1 above.
  if (serverTotalIsStale) {
    totalCents = fallbackCents;
  } else if (blockTotal !== undefined && toCents(blockTotal) > 0) {
    // Priced rows outrank the block total ONLY when they are larger: the Live JOB
    // `payment` subset carries paymentAmount straight from the trip cache, which is
    // still the booking figure on a metered trip that has not been priced yet. A row sum
    // BELOW the block total is the quoted-trip case above — an unreconciled remainder —
    // so the block total stands.
    totalCents = Math.max(toCents(blockTotal), rowTotalCents === null ? 0 : rowTotalCents);
    blockTotalWon = rowTotalCents !== null && toCents(blockTotal) > rowTotalCents;
  } else if (rowTotalCents !== null) {
    totalCents = rowTotalCents;
  }
  if (totalCents === null && fallbackCents > 0) {
    totalCents = fallbackCents;
  }
  if (totalCents === null) {
    const zeroKnown = blockTotal !== undefined || (fallbackFare !== undefined && fallbackFare !== null);
    return build({
      tripCostCents,
      base: 0, tolls: 0, fee: 0, tip: 0, card: 0, wallet: 0,
      hasTolls: false, hasCardSurcharge: false, hasPersistedTip: false,
      total: 0, hasAmount: zeroKnown, source: 'none',
    });
  }

  let tolls = 0;
  let fee = 0;
  let tip = 0;
  let hasTolls = false;
  let hasCardSurcharge = false;
  let hasPersistedTip = false;
  rows.forEach((row) => {
    const kind = rowPaymentItem(row);
    if (kind === 'toll' || kind === 'tolls') { hasTolls = true; tolls += rowAmountCents(row); }
    else if (kind === 'cardsurcharge') { hasCardSurcharge = true; fee += rowAmountCents(row); }
    else if (kind === 'tip') { hasPersistedTip = true; tip += rowAmountCents(row); }
  });

  // Same order as paymentService.summarizeTripRateItems(): tip, then fee, then tolls.
  const card = Math.max(0, totalCents - tip);
  const wallet = Math.max(0, card - fee);
  const base = Math.max(0, wallet - tolls);
  return build({
    tripCostCents,
    base, tolls, fee, tip, card, wallet,
    hasTolls, hasCardSurcharge, hasPersistedTip,
    total: totalCents,
    hasAmount: true,
    source: rows.length && !blockTotalWon ? 'priceItems' : 'total',
  });
}

function build(c) {
  return {
    baseFare: fromCents(c.base),
    tolls: fromCents(c.tolls),
    cardSurcharge: fromCents(c.fee),
    persistedTip: fromCents(c.tip),
    hasTolls: c.hasTolls,
    hasCardSurcharge: c.hasCardSurcharge,
    hasPersistedTip: c.hasPersistedTip,
    cardAmount: fromCents(c.card),
    walletAmount: fromCents(c.wallet),
    total: fromCents(c.total),
    // Database Trip.TripCost, or null when the server did not report one. Kept apart
    // from `total` on purpose: the two legitimately differ and neither substitutes
    // for the other.
    tripCost: c.tripCostCents === null || c.tripCostCents === undefined
      ? null
      : fromCents(c.tripCostCents),
    hasAmount: c.hasAmount,
    source: c.source,
  };
}

/**
 * Receipt `paymentMethod` label from the server's settlement payload or a local cash choice.
 * @returns {'Cash'|'Credit Card'|'Wallet'}
 */
export function receiptPaymentMethod(paymentMethod) {
  const m = String(paymentMethod || '').trim().toLowerCase();
  if (m === 'cash') return 'Cash';
  if (m === 'wallet') return 'Wallet';
  return 'Credit Card';
}

export default resolvePaymentBreakdown;
